import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  Chat,
  Files,
  MagnifyingGlass,
  TrendUp,
  User,
  Users,
} from "@phosphor-icons/react"
import {
  Avatar,
  CodenameTag,
  Divider,
  InputGroup,
  SegmentedControl,
  Tag,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Status } from "~/components/OfferDemandCampaign/Status"
import { TableSummary } from "~/components/TableSummary"
import { siteConfig } from "~/constants/config"
import FilterTypes from "~/constants/FilterTypes"
import { AppRoutes } from "~/constants/routes"
import {
  Order_By,
  useOfferHeaderSummaryQuery,
  useOffersListQuery,
} from "~/generated/graphql"
import {
  OfferFilter,
  OfferFilters,
} from "~/pages/d/offer-management/OfferFilters"

const OfferManagement = () => {
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState(params.get("search") || "")
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250)
  const navigate = useNavigate()
  const page = Number(params.get("page")) || 1
  const [viewPackaging] = useLocalStorage({ key: "viewPackaging" })
  const isPackaging = viewPackaging === "2"

  const [filter, setFilter] = useState<OfferFilter>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.offerFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.offerFilter, JSON.stringify(filter))
  }, [filter])

  const [{ data, fetching }] = useOffersListQuery({
    variables: {
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      orderBy: {
        created_at: Order_By.Desc,
      },
      where: {
        status: {
          _eq: filter?.oth_status?.id,
        },
        material_kind: {
          _eq: isPackaging ? "packaging" : "commodity",
        },
        department: {
          reference_id: {
            _eq: filter?.oth_department?.codename,
          },
        },
        seller: debouncedSearch
          ? {
              _or: [
                {
                  name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                },
              ],
            }
          : undefined,
        commodity: filter?.com_commodity?.codename
          ? {
              reference_id: {
                _eq: filter?.com_commodity?.codename,
              },
            }
          : undefined,

        commodity_variety: filter?.com_variety?.codename
          ? {
              reference_id: {
                _eq: filter?.com_variety?.codename,
              },
            }
          : undefined,
        variety_grade: filter?.com_grade?.codename
          ? {
              reference_id: {
                _eq: filter?.com_grade?.codename,
              },
            }
          : undefined,
        created_at: {
          _gte: filter?.oth_startDate
            ? dayjs(filter?.oth_startDate)?.startOf("day").toDate()
            : undefined,
          _lte: filter?.oth_endDate
            ? dayjs(filter?.oth_endDate)?.endOf("day").toDate()
            : undefined,
        },
      },
    },
    requestPolicy: "network-only",
  })

  const [{ data: offerSummary, fetching: summaryFetching }] =
    useOfferHeaderSummaryQuery({
      variables: {
        materialKind: isPackaging ? "packaging" : "commodity",
      },
    })
  const summaryCards = [
    {
      id: "total",
      title: "Total Active Offers",
      value: offerSummary?.active_offer?.aggregate?.count || 0,
      intentClass: "text-green-300",
      iconBgClass: "bg-[#F6FAE3]",
      icon: Files,
    },
    {
      id: "low",
      title: "Closed Offers",
      value: offerSummary?.closed_offer?.aggregate?.count || 0,

      intentClass: "text-[#1447E6]",
      iconBgClass: "bg-[#EEF5FF]",
      icon: TrendUp,
    },
    {
      id: "out",
      title: "Total Buyer Equiries",
      value: offerSummary?.total_buyer_enquiries?.aggregate?.count || 0,
      intentClass: "text-[#6E11B0]",
      iconBgClass: "bg-[#FAF5FF]",
      icon: Users,
    },
    {
      id: "whatsapp_campaign",
      title: "WhatsApp Campaign",
      value:
        Number(
          ((offerSummary?.total_buyer_enquiries_actioned?.aggregate?.count ||
            0) /
            (offerSummary?.total_buyer_enquiries?.aggregate?.count || 0)) *
            100 || 0
        ).toFixed(2) + "%",
      intentClass: "text-[#F54900]",
      iconBgClass: "bg-[#FFF7ED]",
      icon: Chat,
    },
  ]

  const totalOffers = data?.offers_aggregate?.aggregate?.count || 0

  const COLUMNS = [
    {
      Header: "Created On",
      accessor: "created_at",
      Cell({ value }) {
        return (
          <Cell
            title={dayjs(value).format("DD MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm A")}
          />
        )
      },
    },
    {
      Header: "Pavti No.",
      accessor: "id",
      Cell({ value }: { value: string }) {
        return (
          <Cell
            title={
              <div
                className="cursor-pointer border-b-1 border-dashed border-gray-500 inline-block"
                onClick={() => navigate(`/d/stock-management/offer/${value}`)}
              >
                Pavti #{value}
              </div>
            }
          />
        )
      },
    },
    {
      Header: "Supplier",
      accessor: "seller.user.0",
      Cell({ value }) {
        return (
          <Cell
            title={value?.full_name}
            subtitle={
              <CodenameTag
                leftIcon={<User weight="fill" />}
                referenceId={value?.external_reference_id2}
              />
            }
          />
        )
      },
    },
    {
      Header: "Buyer",
      accessor: "total_enuiry.aggregate.count",
      Cell({ value }) {
        return (
          <Cell
            title={
              <Tag intent="info" className="!rounded-xl">
                {value}
              </Tag>
            }
          />
        )
      },
    },

    {
      Header: "Enquiry",
      accessor: "enquiries",
      Cell({ row: { original } }) {
        const successfulEnquiryCount =
          original?.successful_enuiry.aggregate.count || 0
        const totalEnquiryCount = original?.total_enuiry.aggregate.count || 0
        const percentSuccess = !totalEnquiryCount
          ? 0
          : (successfulEnquiryCount / totalEnquiryCount) * 100

        return (
          <Cell
            title={
              <Tag intent="info" className="!rounded-xl">
                {successfulEnquiryCount}
              </Tag>
            }
            subtitle={`${percentSuccess}% success/response`}
          />
        )
      },
    },
    {
      Header: "Commodity",
      accessor: "commodity",
      Cell({ value, row: { original } }) {
        return (
          <Cell
            title={value.name}
            subtitle={original?.commodity_variety?.name}
          />
        )
      },
    },
    {
      Header: "Grade",
      accessor: "variety_grade",
      Cell({ value }) {
        return (
          <Cell
            title={
              <Tag intent="info" className="!rounded-xl" variant="minimal">
                {value?.name}
              </Tag>
            }
          />
        )
      },
    },
    {
      Header: "Rate",
      accessor: "rate",
      Cell({ value, row: { original } }) {
        if (!value) return <Cell title="-" />
        const max_rate = original?.max_rate
        return (
          <Cell
            titleClassName={classNames("!text-brown-400")}
            title={
              `${inr(value)}/kg` + (max_rate ? ` - (${inr(max_rate)}/kg)` : "")
            }
            subtitle={inr(original?.total_amount)}
          />
        )
      },
    },
    {
      Header: "Offer Qty.",
      accessor: "quantity",
      Cell({ value }: { value: number }) {
        if (!value) return <Cell title="-" />
        return (
          <Cell
            titleClassName={classNames("!text-brown-400")}
            title={`${value?.toLocaleString()} kg`}
          />
        )
      },
    },
    {
      Header: "Order Qty",
      accessor: "order_qty.aggregate.sum.total_quantity",
      Cell({ value }: { value: string }) {
        if (!value) return <Cell title="-" />
        return (
          <Cell
            titleClassName={classNames("!text-brown-400")}
            title={`${value?.toLocaleString()} kg`}
          />
        )
      },
    },
    {
      Header: "Hishob Qty.",
      accessor: "hishob_qty.aggregate.sum.total_quantity",
      Cell({ value }: { value?: string }) {
        if (!value) return <Cell title="-" />
        return (
          <Cell
            titleClassName={classNames("!text-brown-400")}
            title={`${value?.toLocaleString()} kg`}
          />
        )
      },
    },
    {
      Header: "Campaign Start",
      accessor: "start_date_time",
      Cell({ value }: { value?: string }) {
        return (
          <Cell
            title={dayjs(value).format("DD MMM 'YY") || "-"}
            subtitle={dayjs(value).format("hh:mm A") || "-"}
          />
        )
      },
    },
    {
      Header: "Campaign End",
      accessor: "end_date_time",
      Cell({ value }: { value?: string }) {
        return (
          <Cell
            title={dayjs(value).format("DD MMM 'YY") || "-"}
            subtitle={dayjs(value).format("hh:mm A") || "-"}
          />
        )
      },
    },
    {
      Header: "Placed By",
      accessor: "created_by",
      Cell({ value, row }) {
        return (
          <Tooltip
            content={
              <div>
                <p>{value?.full_name || "na"}</p>
                <p className="text-gray-400">
                  {dayjs(row?.original?.created_at).format(
                    "DD MMM 'YY | h:mm a"
                  )}
                </p>
              </div>
            }
          >
            <Avatar
              className="relative text-center"
              name={value?.full_name}
              src={value?.photo?.url}
            />
          </Tooltip>
        )
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell({ row: { original } }) {
        return <Cell title={<Status status={original?.status} />} />
      },
    },
  ]

  return (
    <>
      <header className="grid grid-cols-3 justify-between p-2 border-b-1 border-b-gray-300">
        <SegmentedControl
          value={"offer"}
          onChange={value => {
            if (value === "demand") {
              navigate(AppRoutes.demandManagement)
            } else if (value === "stock-management") {
              navigate(AppRoutes.stockManagement)
            }
          }}
          data={[
            {
              label: "Stock Mgnt.",
              value: "stock-management",
            },
            {
              label: "Offer",
              value: "offer",
            },
            {
              label: "Demand",
              value: "demand",
            },
          ].filter(b => b)}
        />
        <div className="w-[500px] flex items-center gap-3">
          <InputGroup
            className="w-full"
            leftElement={<MagnifyingGlass />}
            inputProps={{
              value: search,
              placeholder: "Search by Supplier",
              onChange(e) {
                const value = e.target.value
                setSearch(value)
                if (value) {
                  params.set("search", value)
                } else {
                  params.delete("search")
                }
                setParams(params)
              },
            }}
          />
          <OfferFilters filter={filter} setFilter={setFilter} />
        </div>
      </header>

      <section className="m-4 bg-white p-4 mb-0 rounded-t-xl">
        <div className="grid grid-cols-4 gap-4">
          <TableSummary summary={summaryCards} loading={summaryFetching} />
        </div>
      </section>

      <section className="mx-4 mb-2 bg-white rounded-b-xl border border-t-0 border-gray-200">
        <Divider />
        <TableView
          relative={false}
          className="!mt-0 overflow-x-auto"
          tableClassName="!mt-0 !min-h-[600px] max-h-[600px] overflow-y-scroll"
          isLoading={fetching}
          columns={COLUMNS as any}
          data={data?.offers || []}
          meta={
            <span className="text-xs text-gray-500">
              {data?.offers_aggregate?.aggregate?.count} results
            </span>
          }
          getHeaderProps={() => {
            return {
              className: "!text-xs !px-1",
            }
          }}
          paginationProps={{
            total: Math.ceil(totalOffers / siteConfig.pageSize),
            page: page,
            onChange(page) {
              params.set("page", page.toString())
              params.set("search", "")
              setParams(params)
            },
          }}
          getCellProps={cell => {
            if (cell?.column?.id !== "actions") {
              return {
                className: " border-b-1 border-gray-100 min-w-[100px]",
              }
            }
            if (cell.column.id === "actions") {
              return {
                className: "sticky right-0 bg-white",
              }
            }
          }}
        />
      </section>
    </>
  )
}

export default OfferManagement
